import {
  Drawer,
  Sheet,
  DialogTitle,
  ModalClose,
  List,
  ListItem,
  Button,
  Stack,
  Divider,
  Select,
  Option,
  SvgIcon,
  Input,
  Typography,
  Checkbox,
  FormControl,
  FormHelperText
} from '@mui/joy';

import { DeleteForever } from '@mui/icons-material';
import { useTranslateText } from '../../lib/ai';
import { useEffect, useState } from 'react';
import { useGetValueByKeyByGroup } from '../../lib/values';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LOCALES_SHORT } from '../../constants/defaults';
import { DEFAULT_LOCALE, LOCALES } from '../../constants/defaults';
import { useUpdateLandingPage, useGetLandingPage } from '../../lib/landing-pages';

const actions = [{ value: 'translate', label: 'Translate' }];

const LandingPageBulk = ({ bulkPage, open, setOpen }) => {
  const queryClient = useQueryClient();

  const [csvStatics, setCsvStatics] = useState({});

  const [openOptions, setOpenOptions] = useState(false);
  const [fromLanguage, setFromLanguage] = useState(DEFAULT_LOCALE);
  const [toLanguage, setToLanguage] = useState(null);
  const [glossary, setGlossary] = useState('');
  const [translating, setTranslating] = useState(false);
  const [changeslug, setChangeslug] = useState(false);
  const [changecontent, setChangecontent] = useState(false);
  const [changecontentConfirmed, setChangecontentConfirmed] = useState(false);

  const [bulk, setBulk] = useState(bulkPage);

  const { refetch } = useQuery(
    [`value`],
    async () => {
      try {
        return await useGetValueByKeyByGroup(
          'TRANSLATE_GLOSSARIES',
          `${LOCALES_SHORT[fromLanguage]}_${LOCALES_SHORT[toLanguage]}`
        );
      } catch (e) {
        return true;
      }
    },
    {
      enabled: false
    }
  );

  const handleTranslate = async () => {
    setTranslating(true);
    setCsvStatics((prev) => ({ ...prev, success: 0, error: 0, errorMessages: [], infoMessages: [] })); // Limpiar resultados anteriores
    window.onbeforeunload = function () {
      return 'Translate working ...';
    };

    for (const page of bulk) {
      try {
        // Validar que el _id no esté vacío
        if (!page._id || page._id.trim() === '') {
          console.warn('Skipping page with empty _id');
          page.success = false;
          page.comment = 'empty _id';
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        console.log(`Processing landing page: ${page._id}`);
        
        const result = await queryClient.fetchQuery({
          queryKey: [`landingPage-${page._id}`],
          queryFn: async () => {
            try {
              return await useGetLandingPage(page._id);
            } catch (e) {
              console.error(`Error fetching landing page ${page._id}:`, e);
              page.success = false;
              page.comment = 'not found';
              return null;
            }
          }
        });

        // Verificar si la respuesta es válida
        if (!result || !result.data || !Array.isArray(result.data) || result.data.length === 0) {
          console.error(`Invalid response for landing page ${page._id}:`, result);
          page.success = false;
          page.comment = result === null ? 'not found' : 'invalid response';
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        const landingPage = result.data[0];
        console.log({ landingPage });
        
        if (!landingPage || !landingPage.i18n) {
          console.error(`Invalid landing page structure for ${page._id}`);
          page.success = false;
          page.comment = 'invalid structure';
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        const landingInfo = landingPage.i18n;

        // Verificar si realmente hay contenido en el idioma de destino
        // Solo considerar que hay contenido si existe el objeto Y tiene un título con texto
        const hasExistingContent = 
          landingInfo[toLanguage] && 
          landingInfo[toLanguage].title && 
          landingInfo[toLanguage].title.trim() !== '';

        console.log(`Checking existing content for ${toLanguage}:`, {
          exists: !!landingInfo[toLanguage],
          title: landingInfo[toLanguage]?.title,
          hasContent: hasExistingContent
        });

        if (!changecontentConfirmed && hasExistingContent) {
          console.log(`Skipping ${page._id} - has existing content in ${toLanguage}`);
          page.success = false;
          const infoMessage = `Landing (${landingInfo[fromLanguage].title} | ${page._id} ) was not translated because overwrite was not selected`;
          page.comment = infoMessage;
          setCsvStatics((prev) => ({ 
            ...prev, 
            infoMessages: [...(prev.infoMessages || []), infoMessage]
          }));
          continue;
        }

        console.log('22', landingInfo, fromLanguage);
        
        if (!landingInfo[fromLanguage] || !landingInfo[fromLanguage].seo) {
          console.error(`Missing ${fromLanguage} data for landing page ${page._id}`);
          page.success = false;
          page.comment = `missing ${fromLanguage} data`;
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        landingInfo[fromLanguage].seo.temp_slug = landingInfo[fromLanguage].seo.slug;

        console.log(`Translating landing page ${page._id} from ${fromLanguage} to ${toLanguage}`);
        
        const translate = await useTranslateText({
          template: `
        I am an expert translator. My goal is to provide the best translation possible. I will translate your text from ${LOCALES[fromLanguage]} to ${LOCALES[toLanguage]}.
        In the JSON, I will find words that are between curly braces like {{ location }}, this words are variables and I don't have to be translate them, they must be kept them in the original language.
        I have a glossary with the words and their translations, and I will use it to translate the text.
      `,
          query: `
        Please, translate this JSON to ${LOCALES[toLanguage]}, translate only the values and not the keys. Respond with the same json but translated. And remember, you need to use the glossary for the translation.
      `,
          data: JSON.stringify(landingInfo[fromLanguage]),
          glossary
        });

        if (!translate || !translate.ok) {
          console.error(`Translation API error for landing page ${page._id}:`, translate);
          page.success = false;
          page.comment = 'translation API error';
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        const translateData = await translate.json();
        
        if (!translateData || !translateData.response) {
          console.error(`Invalid translation response for landing page ${page._id}:`, translateData);
          page.success = false;
          page.comment = 'invalid translation response';
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        const json = translateData.response;

        let parsedResponse;
        try {
          const cleanedJson = json.replace('```json\n', '').replace('\n```', '').trim();
          parsedResponse = JSON.parse(cleanedJson);
        } catch (parseError) {
          console.error(`JSON parse error for landing page ${page._id}:`, parseError, json);
          page.success = false;
          page.comment = 'JSON parse error';
          setCsvStatics((prev) => ({ ...prev, error: (prev.error || 0) + 1 }));
          continue;
        }

        // Get existing slug if it exists
        const existingSlug = landingInfo[toLanguage]?.seo?.slug;
        
        // Calculate expected slug parts based on available data
        const activitySlug = landingPage.activityData?.i18n[toLanguage]?.slug;
        
        // Verificar que landingPage.country?.i18n[toLanguage]?.slug existe
        const countrySlug = landingPage.country?.i18n[toLanguage]?.slug;
        const locationSlug = landingPage.locationData?.i18n[toLanguage]?.slug;
        const regionSlug = landingPage.regionData?.i18n[toLanguage]?.slug; 

        console.log('locationSlug:', locationSlug);
        console.log('regionSlug:', regionSlug);
        console.log('countrySlug:', countrySlug);
        console.log('activitySlug:', activitySlug);
        
        // Verificaciones de existencia de slugs
        // Si el type es "ACTIVITY", no se requiere countrySlug
        const landingPageType = landingPage.type;
        if ((!countrySlug || countrySlug === '' || countrySlug === null || countrySlug === undefined) && landingPageType !== 'ACTIVITY') {
          const countryId = landingPage.country?._id || landingPage.country || 'N/A';
          const landingPageId = page._id || 'N/A';
          const landingPageCode = landingInfo[fromLanguage]?.seo?.slug || landingInfo[fromLanguage]?.title || 'N/A';
          throw new Error(`Landing Page (ID: ${landingPageId}, Code: ${landingPageCode}) - Country (ID: ${countryId}) does not have translation for destination language (${toLanguage}). Cannot continue with translation. This issue can NOT be fixed from admin site and requires dev team actions.`);
        }

        if (!locationSlug || locationSlug === '' || locationSlug === null || locationSlug === undefined) {
          const locationId = landingPage.locationData?._id || landingPage.location || 'N/A';
          //throw new Error(`Location (ID: ${locationId}) does not have translation for source language (${fromLanguage}). Cannot continue with translation. This issue can NOT be fixed from admin site and requires dev team actions.`);
          console.log(`Location (ID: ${locationId}) does not have translation for source language (${fromLanguage}). Cannot continue with translation. This issue can NOT be fixed from admin site and requires dev team actions.`);
        }
        
        // Verificar que regionSlug existe (si regionData existe, debe tener slug)
        if (landingPage.regionData && (!regionSlug || regionSlug === '' || regionSlug === null || regionSlug === undefined)) {
          const regionId = landingPage.regionData?._id || landingPage.region || 'N/A';
          //throw new Error(`Region (ID: ${regionId}) does not have translation for source language (${fromLanguage}). Cannot continue with translation. This issue can NOT be fixed from admin site and requires dev team actions.`);
          console.log(`Region (ID: ${regionId}) does not have translation for source language (${fromLanguage}). Cannot continue with translation. This issue can NOT be fixed from admin site and requires dev team actions.`);
        }
        
        // Si hay activity, usar countrySlug directamente, si no, agregar prefijo "trips-"
        const countrySlugFinal = activitySlug ? countrySlug : (countrySlug ? `trips-${countrySlug}` : null);
        console.log('countrySlugFinal:', countrySlugFinal);
        
        // Calculate how many parts the slug should have
        const expectedParts = [activitySlug, countrySlugFinal, locationSlug || regionSlug].filter(Boolean).length;
        const existingParts = existingSlug ? existingSlug.split('/').filter(Boolean).length : 0;
        
        // Build complete pathname if changeslug is true, slug doesn't exist, or slug is incomplete
        let pathname = existingSlug;
        
        if (changeslug || !existingSlug || existingParts < expectedParts) {
          // Construct the complete pathname: activity/country/location
          pathname = [activitySlug, countrySlugFinal, locationSlug || regionSlug]
            .filter(Boolean)
            .join('/');
        }

        console.log({ pathname });

        const locale = toLanguage.split('_')[0];
        const pathnameWithLocale =
          toLanguage === DEFAULT_LOCALE ? pathname : `${locale}/${pathname}`;
        const link = `${import.meta.env.VITE_SITE}/${pathnameWithLocale}/`;

        delete parsedResponse.seo.temp_slug;

        const finalData = {
          i18n: {
            ...landingInfo,
            [toLanguage]: {
              ...parsedResponse,
              seo: { ...parsedResponse.seo, link, slug: pathname },
              tags: [{ key: 'bulk-translate', value: new Date() }]
            }
          }
        };

        console.log({ finalData });

        console.log(`Updating landing page ${page._id}`);
        await useUpdateLandingPage(page._id, finalData);
        page.success = true;
        page.url = link;
        if (landingInfo[toLanguage]) {
          page.oldLink = landingInfo[toLanguage].seo.link;
        }
        setCsvStatics((prev) => ({ ...prev, success: (prev.success || 0) + 1 }));
        console.log(`Successfully translated landing page ${page._id}`);
      } catch (e) {
        console.error(`Error processing landing page ${page._id}:`, e);
        page.success = false;
        page.comment = e.message || 'unknown error';
        setCsvStatics((prev) => ({ 
          ...prev, 
          error: (prev.error || 0) + 1,
          errorMessages: [...(prev.errorMessages || []), e.message || 'unknown error']
        }));
      }
    }

    console.log('Translation process completed');
    setTranslating(false);
    window.onbeforeunload = null;
  };

  useEffect(() => {
    if (!fromLanguage || !toLanguage) return;
    refetch().then(({ data }) => setGlossary(data.value));
  }, [fromLanguage, toLanguage]);

  const handleChange = (event, newValue) => {
    setOpenOptions(newValue);
  };

  const handleUploadFile = (e) => {
    console.log(e.target.files);

    var reader = new FileReader();
    reader.onload = function (progressEvent) {
      // Entire file
      const text = this.result;

      // By lines
      var lines = text.split('\n');
      
      // Eliminar encabezado
      lines.shift();

      // Filtrar líneas vacías y espacios en blanco, y limpiar cada línea
      const validLines = lines
        .map((item) => item.trim())
        .filter((item) => item !== '' && item.length > 0);

      console.log('Valid IDs:', validLines);
      setBulk(validLines.map((item) => ({ _id: item })));
      setCsvStatics({ count: validLines.length });
    };
    reader.readAsText(e.target.files[0]);
  };

  return (
    <Drawer
      size="md"
      variant="plain"
      open={open}
      onClose={() => {
        if (!translating) {
          setOpen(false);
        }
      }}
      slotProps={{
        content: {
          sx: {
            bgcolor: 'transparent',
            p: { md: 3, sm: 0 },
            boxShadow: 'none'
          }
        }
      }}>
      <Sheet
        sx={{
          borderRadius: 'md',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          overflow: 'auto'
        }}>
        <DialogTitle>Bulk Action</DialogTitle>
        <ModalClose />

        {bulkPage.length ? (
          <List>
            {bulkPage.map((item) => (
              <ListItem
                endAction={
                  <Button size="sm">
                    <DeleteForever />
                  </Button>
                }>
                {item.title}
              </ListItem>
            ))}
          </List>
        ) : (
          <>
            <Typography level="body-sm">Upload a csv with _id column (need header)</Typography>
            <Button
              component="label"
              role={undefined}
              tabIndex={-1}
              variant="outlined"
              color="neutral"
              startDecorator={
                <SvgIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                    />
                  </svg>
                </SvgIcon>
              }>
              Upload a file
              <Input
                type="file"
                onChange={handleUploadFile}
                sx={{
                  clip: 'rect(0 0 0 0)',
                  clipPath: 'inset(50%)',
                  height: '1px',
                  overflow: 'hidden',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  whiteSpace: 'nowrap',
                  width: '1px'
                }}
              />
            </Button>
            {console.log(csvStatics)}
            {csvStatics.count ? (
              <Typography color="neutral">Rows found {csvStatics.count}</Typography>
            ) : null}
            {csvStatics.success ? (
              <Typography color="success">Rows success {csvStatics.success}</Typography>
            ) : null}
            {csvStatics.error ? (
              <>
                <Typography color="danger">Rows fail {csvStatics.error}</Typography>
                {csvStatics.errorMessages && csvStatics.errorMessages.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {csvStatics.errorMessages.map((errorMsg, index) => (
                      <Typography
                        key={index}
                        sx={{
                          bgcolor: '#ef4444',
                          color: 'white',
                          p: 1,
                          borderRadius: 'sm',
                          fontSize: '1rem',
                          fontWeight: 600
                        }}>
                        {errorMsg}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </>
            ) : null}
            {csvStatics.infoMessages && csvStatics.infoMessages.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {csvStatics.infoMessages.map((infoMsg, index) => (
                  <Typography
                    key={index}
                    sx={{
                      bgcolor: '#fd7e14',
                      color: 'white',
                      p: 1,
                      borderRadius: 'sm',
                      fontSize: '1rem',
                      fontWeight: 600
                    }}>
                    {infoMsg}
                  </Typography>
                ))}
              </Stack>
            )}
          </>
        )}

        <Divider sx={{ mt: 'auto' }} />
        <Stack direction="column" justifyContent="space-between" useFlexGap spacing={1}>
          <Select placeholder="Choose one…" onChange={handleChange}>
            {actions.map((item) => (
              <Option value={item.value}>{item.label}</Option>
            ))}
          </Select>

          {openOptions === 'translate' && (
            <>
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Select
                  defaultValue={DEFAULT_LOCALE}
                  sx={{ flex: 1 }}
                  placeholder="Translate From .."
                  onChange={(event, newValue) => setFromLanguage(newValue)}>
                  {Object.entries(LOCALES_SHORT).map((item) => (
                    <Option value={item[0]}>{item[1]}</Option>
                  ))}
                </Select>

                <Select
                  sx={{ flex: 1 }}
                  placeholder="Translate to .."
                  onChange={(event, newValue) => setToLanguage(newValue)}>
                  {Object.entries(LOCALES_SHORT).map((item) =>
                    DEFAULT_LOCALE !== item[0] ? <Option value={item[0]}>{item[1]}</Option> : null
                  )}
                </Select>
              </Stack>

              <FormControl>
                <Checkbox
                  label="Overwrite translation"
                  size="sm"
                  color="danger"
                  onChange={(event) => setChangecontent(event.target.checked)}
                />
              </FormControl>
              {!changecontent ? (
                <FormHelperText level="body-xs">
                  If you check this option, the translator will ignore pre-existing content.
                </FormHelperText>
              ) : (
                <FormHelperText level="body-xs">
                  <Input
                    placeholder="type `overwrite` for confirmation"
                    onChange={(event) =>
                      setChangecontentConfirmed(event.target.value === 'overwrite')
                    }
                  />
                </FormHelperText>
              )}

              {changecontentConfirmed ? (
                <FormControl>
                  <Checkbox
                    label="Change slug"
                    size="sm"
                    color="danger"
                    onChange={(event) => setChangeslug(event.target.checked)}
                  />
                  <FormHelperText level="body-xs">
                    If you check this option, the slug and URL will be changed.
                  </FormHelperText>
                </FormControl>
              ) : null}
            </>
          )}

          <Button onClick={() => handleTranslate()} loading={translating}>
            Run
          </Button>
        </Stack>
      </Sheet>
    </Drawer>
  );
};

export default LandingPageBulk;
